import React, { useEffect, useState, Fragment } from "react";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  Link,
  MenuItem,
  Select,
  Typography
} from "@mui/material";

import DrawerCustom from "../../../../shared/general/Drawer";
import TextFieldTheme from "../../../../shared/general/TextFieldTheme";

import AddIconImage from "../../../../assets/add.png";
import AddIcon from "@mui/icons-material/Add";

import { getAllHolidayAndAllHolidayPattern, uploadFileHoliday } from "../../../../../../actions/holiday";

import ShowResponse from "./show-response";
import FileSelected from "./file-selected";

const StyledRoot = styled("div")({
  width: 550,
  padding: 24,
});

const StyledImportFile = styled("div")({
  maxWidth: 450,
  margin: "auto",
  marginBottom: 24,
  marginTop: 24,
  "& .dropzone-upload-file": {
    textAlign: "center",
    backgroundColor: "#F9F9FB",
    border: "1px solid #D0D0D0",
    borderStyle: "dashed",
    borderRadius: 8,
    height: 200,
    display: "flex",
    justifyContent: "center",
    "& .inner-dropzone": {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      "& img": {
        marginBottom: 16,
      },
    },
  },
});

const StyledLoading = styled("div")({
  height: 200,
  maxWidth: 450,
  margin: "auto",
  marginBottom: 24,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
});

const StyledContentLabel = styled(Typography)({
  fontWeight: 600,
  fontSize: 16,
  marginBottom: "8px"
});

const FormControlStyled = styled(FormControl)((disabled) => ({
  "& .MuiFilledInput-root": {
    backgroundColor: "#919eab14",
    height: 56,
    padding: "0px 12px",
    borderRadius: 8,
    "&.Mui-focused": {
      backgroundColor: "#919eab14",
    },
    "& .MuiSelect-select": {
      padding: "4px 0 5px 8px",
    },
    "&:hover": {
      ...(disabled ? {} : { backgroundColor: "#919eab29" }),
      "&:before": {
        border: "none !important",
      },
    },
    "&::after": {
      border: "none",
    },
    "&::before": {
      border: "none",
      borderBottomStyle: "none !important",
    },
  },
}));

const MenuItemAddHolidayPattern = styled(MenuItem)({
  padding: "0",
  margin: "16px 16px 6px",
  borderRadius: "8px",
  backgroundColor: "#007afe",
  color: "#ffffff",
  "&:hover":{
    backgroundColor: '#0046b7'
  },
  "&.Mui-selected":{
    backgroundColor: '#007afe',
    "&:hover":{
      backgroundColor: '#0046b7'
    },
  }
});

const StyledBoxContent = styled("div")({
  marginBottom: "16px"
});

const DialogUpload = (props) => {
  const { open, toggleDrawer } = props;
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { result: holidayStore } = useSelector((state) => state.holiday);
  
  const [year, setYear] = useState(`${dayjs().year()}`);
  const [holidayPattern, setHolidayPattern] = useState({
    idHolidayPattern: null,
    holidayPatternName: null,
    error: null
  });
  const [listHolidayPattern, setListHolidayPattern] = useState([]);
  const [fileSelected, setFileSelected] = useState(null);
  const [formFileSelected, setFormFileSelected] = useState(null);
  const [openLoading, setOpenLoading] = useState(false);
  const [errorResponse, setErrorResponse] = useState(null);

  useEffect(() => {
    filterHolidayPattern();
  },[open, holidayStore, year]);

  useEffect(() => {
    setYear(`${dayjs().year()}`);
    if(errorResponse){
      setErrorResponse(null);
    }
  },[open]);

  useEffect(() => {
    if(listHolidayPattern.length > 0){
      setHolidayPattern({
        idHolidayPattern: listHolidayPattern[0].idHolidayPattern,
        holidayPatternName: null,
        error: null
      });
    }else{
      setHolidayPattern({
        idHolidayPattern: "new",
        holidayPatternName: "",
        error: null
      });
    }
    setFileSelected(null);
  },[open, listHolidayPattern]);

  const { getRootProps, getInputProps } = useDropzone({
    accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    onDrop: (acceptedFiles) => {
      const formData = new FormData();
      acceptedFiles.map((file) => {
        formData.append("file", file);
        formData.append("year", year);
        formData.append("idHolidayPattern", holidayPattern.idHolidayPattern);
        formData.append("holidayPatternName", holidayPattern.holidayPatternName || null);
      });
      setFormFileSelected(formData);
      setFileSelected({
        name: acceptedFiles[0].name,
        size: acceptedFiles[0].size,
      });
    },
    maxFiles: 1,
  });

  const handleClickUpload = async () => {
    setOpenLoading(true);

    if(
      holidayPattern.idHolidayPattern === "new" && 
      holidayPattern.holidayPatternName === ""
    ){
      setHolidayPattern({
        ...holidayPattern,
        error: { message: t("ThisFieldIsRequired") }
      });
      setOpenLoading(false);
      return;
    }

    const result = await dispatch(uploadFileHoliday(formFileSelected));
    if (result) {
      if(result.status === 200){
        dispatch(getAllHolidayAndAllHolidayPattern(new Date().getFullYear()));
      }
      setErrorResponse(result.data);
      setOpenLoading(false);
    }
  };

  const filterHolidayPattern = () => {
    if(holidayStore && Object.keys(holidayStore).length > 0 && holidayStore[year]){
      setListHolidayPattern(holidayStore[year]);
    }else{
      setListHolidayPattern([]);
    }
  };

  const handleClickDownload = () => {
    window.open(`${process.env.REACT_APP_API_URL}files/Template_Holiday.xlsx`);
  };

  return (
    <DrawerCustom
      title={t("UploadHoliday")}
      anchor={"right"}
      open={open}
      onClose={toggleDrawer(false)}
    >
      <StyledRoot>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {openLoading ? (
              <StyledLoading>
                <Typography variant="h6" color="text.third" gutterBottom>
                  {`${t("Saving_Data")}...`}
                </Typography>
                <div>
                  <CircularProgress />
                </div>
              </StyledLoading>
            ) : (
              <Fragment>
                {errorResponse ? (
                  <ShowResponse
                    errorResponse={errorResponse}
                    handleCloseLoading={toggleDrawer(false)}
                  />
                ) : (
                  <Fragment>
                    <StyledBoxContent>
                      <StyledContentLabel color="text.third" gutterBottom>
                        {t("Holiday")}
                      </StyledContentLabel>
                      <FormControlStyled variant="filled" fullWidth>
                        <Select
                          value={year}
                          onChange={(e) => {
                            setYear(e.target.value);
                          }}
                        >
                          <MenuItem value={dayjs().format("YYYY")}>{dayjs().format(i18n.language === "th" ? "BBBB" : "YYYY")}</MenuItem>
                          <MenuItem value={dayjs().add(1,'year').format("YYYY")}>{dayjs().add(1,'year').format(i18n.language === "th" ? "BBBB" : "YYYY")}</MenuItem>
                        </Select>
                      </FormControlStyled>
                    </StyledBoxContent>
                    <StyledBoxContent>
                      <StyledContentLabel color="text.third" gutterBottom>
                        {t("HolidayPattern")}
                      </StyledContentLabel>
                      <FormControlStyled variant="filled" fullWidth>
                        <Select
                          value={holidayPattern.idHolidayPattern}
                          readOnly={(listHolidayPattern.length <= 0) ? true : false}
                          onChange={(e) => {
                            let value = e.target.value;

                            setHolidayPattern({
                              idHolidayPattern: value,
                              holidayPatternName: (value === "new") ? "" : null,
                              error: null
                            });
                            setFileSelected(null);
                          }}
                        >
                          {listHolidayPattern.length > 0 ? (
                            listHolidayPattern.map((hp) => (
                              <MenuItem key={hp.idHolidayPattern} value={hp.idHolidayPattern}>
                                {hp.holidayPatternName}
                              </MenuItem>
                            ))
                          ) : (
                            <Fragment></Fragment>
                          )}
                          <MenuItemAddHolidayPattern 
                            value="new"
                          >
                            <Box 
                              width="100%"
                              display="flex" 
                              alignItems="center" 
                              justifyContent="center"
                              padding="8px 0px"
                            >
                              <AddIcon style={{ marginRight: "8px" }} />{t("AddHolidayPattern")}
                            </Box>
                          </MenuItemAddHolidayPattern>
                        </Select>
                      </FormControlStyled>
                    </StyledBoxContent>
                    {(holidayPattern.idHolidayPattern === "new") ? (
                      <StyledBoxContent>
                        <StyledContentLabel color="text.third" gutterBottom>
                          {t("NameOfHolidayPattern")}
                        </StyledContentLabel>
                        <TextFieldTheme
                          value={holidayPattern.holidayPatternName}
                          onChange={(e) => {
                            setHolidayPattern({
                              ...holidayPattern,
                              holidayPatternName: e.target.value,
                              error: null 
                            })
                          }}
                          variant="outlined"
                          multiline
                          error={holidayPattern.error ? true : false}
                          helperText={(holidayPattern.error && holidayPattern.error.message) || ""}
                        />
                      </StyledBoxContent>
                      ) : (<Fragment></Fragment>)
                    }
                    <StyledBoxContent>
                      <div
                        style={{
                          marginTop: "16px",
                          marginBottom: "16px",
                          display: "flex",
                          justifyContent: "flex-end",
                          cursor: "pointer"
                        }}
                      >
                        <Link 
                          variant="body2" 
                          onClick={handleClickDownload}
                        >
                          {t("DownloadTemplate")}
                        </Link>
                      </div>
                    </StyledBoxContent>
                    <div>
                      <StyledImportFile>
                        <div
                          {...getRootProps({
                            className: "dropzone-upload-file",
                          })}
                        >
                          <input {...getInputProps()} />
                          <div className="inner-dropzone">
                            <img alt="AddIcon" src={AddIconImage} width="80" />
                            <Typography
                              style={{
                                marginTop: 8,
                                backgroundColor: "transparent",
                              }}
                              className={`${
                                fileSelected != 0 && `placeholderLabel`
                              }`}
                              variant="body2"
                              color="text.secondary"
                            >
                              {t("DragAndDropAFileHereOrClickToBrowse")}
                            </Typography>
                          </div>
                        </div>
                      </StyledImportFile>
                    </div>

                    {fileSelected && (
                      <FileSelected
                        fileSelected={fileSelected}
                        handleClickUpload={handleClickUpload}
                      />
                    )}
                  </Fragment>
                )}
              </Fragment>
            )}
          </Grid>
        </Grid>
      </StyledRoot>
    </DrawerCustom>
  );
};

export default DialogUpload;
